import { useEffect, useMemo, useState } from "react";
import { Button } from "../components/ui/button";
import BusinessCaseForm from "../components/business-case/BusinessCaseForm";
import ResultsSummary from "../components/business-case/ResultsSummary";
import ComparisonTable from "../components/business-case/ComparisonTable";
import BusinessCasePreview, { buildBusinessCaseText } from "../components/business-case/BusinessCasePreview";
import { benchmarkDefaults, BUSINESS_CASE_STORAGE_KEY } from "../components/business-case/defaults";
import { calculateBusinessCase, formatCurrencyGbp, formatNumber } from "../components/business-case/calculations";
import { validateBusinessCase } from "../components/business-case/validation";
import { downloadBusinessCasePdf } from "../components/business-case/BusinessCasePdf";
import { toast } from "sonner";
import { useSeo } from "../lib/useSeo";

const loadLocal = () => {
  try {
    const raw = localStorage.getItem(BUSINESS_CASE_STORAGE_KEY);
    if (!raw) return benchmarkDefaults;
    const parsed = JSON.parse(raw);
    return { ...benchmarkDefaults, ...(parsed || {}) };
  } catch {
    return benchmarkDefaults;
  }
};

export default function EmployerBusinessCaseBuilder() {
  const [values, setValues] = useState(() => loadLocal());

  useSeo({
    title: "Talent Pool Business Case Builder | Railway Careers UK",
    description:
      "Estimate recruitment capacity, interview demand and modelled value differences between conventional campaigns and the Railway Careers UK Talent Pool route.",
    canonical: "/for-employers/business-case-builder",
  });

  useEffect(() => {
    localStorage.setItem(BUSINESS_CASE_STORAGE_KEY, JSON.stringify(values));
  }, [values]);

  const warnings = useMemo(() => validateBusinessCase(values), [values]);
  const calc = useMemo(() => calculateBusinessCase(values), [values]);

  const setValue = (field, value) => {
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const copyDraft = async () => {
    try {
      await navigator.clipboard.writeText(buildBusinessCaseText(values, calc));
      toast.success("Draft business case copied");
    } catch {
      toast.error("Could not copy draft text");
    }
  };

  const printCase = () => {
    window.print();
  };

  const resetDefaults = () => {
    setValues({ ...benchmarkDefaults });
    toast.success("Benchmark assumptions restored");
  };

  const clearSaved = () => {
    localStorage.removeItem(BUSINESS_CASE_STORAGE_KEY);
    setValues({ ...benchmarkDefaults });
    toast.success("Saved local assumptions cleared");
  };

  const recalc = () => {
    setValues((prev) => ({ ...prev }));
    toast.success("Results recalculated");
  };

  return (
    <div className="min-h-screen bg-sand">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-8 md:py-12">
        <header className="max-w-4xl">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-amber">For employers</div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl md:text-5xl tracking-tight text-ink mt-2 leading-tight">
            Talent Pool Business Case Builder
          </h1>
          <p className="mt-4 text-muted-foreground text-base md:text-lg leading-relaxed">
            Enter your recruitment volumes, salaries and working assumptions to estimate hours saved, indicative internal value, and net value after Talent Pool fees.
          </p>
          <div className="mt-4 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
            Calculations are completed within your browser. Information entered into this tool is not submitted to Railway Careers UK unless a future server-side saving feature is explicitly introduced.
          </div>
        </header>

        <div className="mt-6 md:mt-8 grid lg:grid-cols-[1.08fr_0.92fr] gap-5 md:gap-6 items-start">
          <div className="space-y-5 order-2 lg:order-1">
            <BusinessCaseForm values={values} setValue={setValue} warnings={warnings} />
          </div>

          <div className="space-y-5 order-1 lg:order-2 lg:sticky lg:top-24">
            <ResultsSummary calc={calc} />
            <section className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-display font-bold text-xl text-ink">Loaded hourly rates</h3>
              <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-border p-3">
                  <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Recruitment / HR</div>
                  <div className="mt-1 text-ink font-semibold">Salary-only: {formatCurrencyGbp(calc.hrRates.salaryOnlyHourlyRate, 2)}</div>
                  <div className="text-ink font-semibold">Loaded: {formatCurrencyGbp(calc.hrRates.loadedHourlyRate, 2)}</div>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Operational manager</div>
                  <div className="mt-1 text-ink font-semibold">Salary-only: {formatCurrencyGbp(calc.operationalRates.salaryOnlyHourlyRate, 2)}</div>
                  <div className="text-ink font-semibold">Loaded: {formatCurrencyGbp(calc.operationalRates.loadedHourlyRate, 2)}</div>
                </div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Productive annual hours: {formatNumber(calc.hrRates.productiveAnnualHours, 1)} based on {values.weeklyContractedHours} weekly hours and {values.productiveWeeksPerYear} productive weeks.
              </p>
            </section>

            <ComparisonTable values={values} calc={calc} />
            <BusinessCasePreview values={values} calc={calc} />

            <section className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-display font-bold text-lg text-ink">Actions</h3>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Button onClick={() => downloadBusinessCasePdf(values, calc)} className="w-full bg-navy text-bone hover:bg-lime-600 hover:text-ink">
                  Download PDF business case
                </Button>
                <Button variant="outline" onClick={copyDraft} className="w-full">Copy draft text</Button>
                <Button variant="outline" onClick={printCase} className="w-full">Print business case</Button>
                <Button variant="outline" onClick={resetDefaults} className="w-full">Reset benchmark assumptions</Button>
                <Button variant="outline" onClick={recalc} className="w-full">Recalculate</Button>
                <Button variant="outline" onClick={clearSaved} className="w-full sm:col-span-2">Clear saved local values</Button>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Local storage: your latest entries are saved in this browser only. No calculator inputs are sent to the backend.
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
